import { DiffEditor, Monaco } from "@monaco-editor/react";
import SaveIcon from "@mui/icons-material/Save";
import { Button, Stack, Typography } from "@mui/material";
import { editor } from "monaco-editor";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import alqemiDataService from "../../api/AlqemiDataService";
import CircularLoader from "../../common/components/fundamentals/Loader/CircularLoader";
import { useSnackbar } from "../../common/context/SnackbarProvider";
import { getDefaultCV } from "../../utils/cvUtils";
import { alignArraysToReference, canonicalizeKeys } from "./cvDiffOrdering";

const propertyOrder = [
  "fullName",
  "jobTitle",
  "location",
  "nationality",
  "headlines",
  "certificates",
  "certificates_logos",
  "education",
  "languages",
  "professionalCareer",
  "projects",
  "tech",
  "requirementsMatch",
];

interface SchemaField {
  type: "string" | "number" | "array" | "object" | "boolean" | "nullable";
  locked?: boolean;
  value?: any;
}

interface CvSchema {
  [key: string]: SchemaField;
}

interface ValidationError {
  message: string;
  line: number;
}

function captureSchema(template: Record<string, any>): CvSchema {
  const schema: CvSchema = {};

  for (const key of Object.keys(template)) {
    const value = template[key];
    let type: SchemaField["type"];

    if (value === null || value === undefined) {
      type = "nullable";
    } else if (Array.isArray(value)) {
      type = "array";
    } else {
      type = typeof value as SchemaField["type"];
    }

    schema[key] = { type };

    if (key === "fullName") {
      schema[key].locked = true;
      schema[key].value = value;
    }
  }

  // Include propertyOrder keys not in template (e.g. requirementsMatch added by matching)
  for (const key of propertyOrder) {
    if (!(key in schema)) {
      schema[key] = { type: "nullable" };
    }
  }

  return schema;
}

function findLineForKey(jsonString: string, key: string): number {
  const lines = jsonString.split("\n");
  const pattern = new RegExp(`^\\s*"${key}"\\s*:`);
  for (let i = 0; i < lines.length; i++) {
    if (pattern.test(lines[i])) {
      return i + 1; // Monaco lines are 1-indexed
    }
  }
  return 1;
}

function validateCvEdit(jsonString: string, schema: CvSchema): ValidationError[] {
  const errors: ValidationError[] = [];

  // Rule 1: JSON parse check
  let parsed: Record<string, any>;
  try {
    parsed = JSON.parse(jsonString);
  } catch (e) {
    let line = 1;
    const match = String(e).match(/position\s+(\d+)/i);
    if (match) {
      const position = parseInt(match[1], 10);
      const upToError = jsonString.substring(0, position);
      line = (upToError.match(/\n/g) || []).length + 1;
    }
    errors.push({ message: "Invalid JSON syntax", line });
    return errors;
  }

  // Rule 2: fullName immutability
  const fullNameField = schema.fullName;
  if (fullNameField?.locked && parsed.fullName !== fullNameField.value) {
    errors.push({
      message: "fullName cannot be changed",
      line: findLineForKey(jsonString, "fullName"),
    });
  }

  // Rule 3: No missing keys
  for (const key of Object.keys(schema)) {
    if (!(key in parsed)) {
      errors.push({
        message: `Missing required field: ${key}`,
        line: 1,
      });
    }
  }

  // Rule 4: No extra top-level keys
  for (const key of Object.keys(parsed)) {
    if (!(key in schema)) {
      errors.push({
        message: `Unknown field: ${key}`,
        line: findLineForKey(jsonString, key),
      });
    }
  }

  // Rule 5: Type consistency
  for (const [key, field] of Object.entries(schema)) {
    if (!(key in parsed)) continue;
    const value = parsed[key];

    if (field.type === "nullable") {
      continue;
    }

    if (field.type === "array" && !Array.isArray(value)) {
      errors.push({
        message: `Field ${key} must be an array`,
        line: findLineForKey(jsonString, key),
      });
    } else if (field.type === "string" && typeof value !== "string") {
      errors.push({
        message: `Field ${key} must be a string`,
        line: findLineForKey(jsonString, key),
      });
    } else if (field.type === "number" && typeof value !== "number") {
      errors.push({
        message: `Field ${key} must be a number`,
        line: findLineForKey(jsonString, key),
      });
    } else if (field.type === "object" && (typeof value !== "object" || value === null || Array.isArray(value))) {
      errors.push({
        message: `Field ${key} must be an object`,
        line: findLineForKey(jsonString, key),
      });
    }
  }

  return errors;
}

export interface CvJsonDiffComparerHandle {
  save: () => Promise<void>;
}

const CvJsonDiffComparer = forwardRef<CvJsonDiffComparerHandle, {
  cvContent: any;
  displayedMatch: any;
  setCvContent: (cv: any) => void;
  offer: any;
  setOffer: (offer: any) => void;
  onUnsavedChangesChange?: (hasUnsavedChanges: boolean) => void;
}>(({
  cvContent,
  displayedMatch,
  setCvContent,
  offer,
  setOffer,
  onUnsavedChangesChange,
}, ref) => {
  const { showSnackbar } = useSnackbar();
  const [cvTemplateFull, setCvTemplateFull] = useState<any>(null);
  const [normalizedCvContent, setNormalizedCvContent] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const editorRef = useRef<editor.IDiffEditor | null>(null);
  const monacoRef = useRef<Monaco | null>(null);
  const cvSchemaRef = useRef<CvSchema | null>(null);
  const handleSaveChangesRef = useRef<() => Promise<void>>();

  useImperativeHandle(ref, () => ({
    save: handleSaveChanges,
  }));

  useEffect(() => {
    if (!displayedMatch?.consultant_id) {
      setIsLoading(false);
      return;
    }

    const fetchCandidatesCvTemplate = async () => {
      setIsLoading(true);
      const candidates = await alqemiDataService.getCandidates();
      const candidate = candidates.find(
        (candidate) => candidate.consultant_id === displayedMatch.consultant_id
      );
      if (candidate) {
        const cvContent = getDefaultCV(candidate);
        setCvTemplateFull(cvContent);
        cvSchemaRef.current = captureSchema(cvContent);
      }
      setIsLoading(false);
    };

    fetchCandidatesCvTemplate();
  }, [displayedMatch?.consultant_id]);

  useEffect(() => {
    if (!cvContent || !cvTemplateFull) return;

    const normalized = {
      fullName: cvContent.fullName,
      jobTitle: cvContent.jobTitle,
      location: cvContent.location,
      nationality: cvContent.nationality,
      headlines: cvContent.headlines || [],
      certificates: cvContent.certificates || [],
      certificates_logos: cvContent.certificates_logos || [],
      education: cvContent.education || [],
      languages: cvContent.languages || [],
      professionalCareer: cvContent.professionalCareer || [],
      projects: cvContent.projects || [],
      tech: cvContent.tech || [],
      requirementsMatch: cvContent.requirementsMatch || null,
    };

    setNormalizedCvContent(normalized);
  }, [cvContent, cvTemplateFull]);

  const handleEditorDidMount = (diffEditor: editor.IDiffEditor, monaco: Monaco) => {
    editorRef.current = diffEditor;
    monacoRef.current = monaco;
    setHasUnsavedChanges(false);
    onUnsavedChangesChange?.(false);

    let validationTimeout: ReturnType<typeof setTimeout> | null = null;

    diffEditor.getModifiedEditor().onDidChangeModelContent(() => {
      setHasUnsavedChanges(true);
      onUnsavedChangesChange?.(true);

      if (validationTimeout) clearTimeout(validationTimeout);
      validationTimeout = setTimeout(() => {
        if (!cvSchemaRef.current || !monacoRef.current) return;
        const modifiedValue = diffEditor.getModifiedEditor().getValue();
        const errors = validateCvEdit(modifiedValue, cvSchemaRef.current);

        const model = diffEditor.getModifiedEditor().getModel();
        if (model) {
          const markers = errors.map((err) => ({
            startLineNumber: err.line,
            startColumn: 1,
            endLineNumber: err.line,
            endColumn: model.getLineMaxColumn(err.line),
            message: err.message,
            severity: monacoRef.current!.MarkerSeverity.Error,
          }));
          monacoRef.current!.editor.setModelMarkers(model, "cv-validation", markers);
        }
      }, 500);
    });

    diffEditor.getModifiedEditor().onKeyDown((e) => {
      if ((e.ctrlKey || e.metaKey) && e.code === "KeyS") {
        e.preventDefault();
        handleSaveChangesRef.current?.();
      }
    });
  };

  const handleSaveChanges = async () => {
    if (!editorRef.current || !displayedMatch) return;

    const modifiedValue = editorRef.current.getModifiedEditor().getValue();

    // Run validation immediately (no debounce) on save
    if (cvSchemaRef.current) {
      const errors = validateCvEdit(modifiedValue, cvSchemaRef.current);

      if (monacoRef.current) {
        const model = editorRef.current.getModifiedEditor().getModel();
        if (model) {
          const markers = errors.map((err) => ({
            startLineNumber: err.line,
            startColumn: 1,
            endLineNumber: err.line,
            endColumn: model.getLineMaxColumn(err.line),
            message: err.message,
            severity: monacoRef.current!.MarkerSeverity.Error,
          }));
          monacoRef.current!.editor.setModelMarkers(model, "cv-validation", markers);
        }
      }

      if (errors.length > 0) {
        const errorMessages = errors.map((e) => e.message).join("; ");
        showSnackbar(`Cannot save: ${errorMessages}`, true);
        return;
      }
    }

    let updatedContent: any;
    try {
      updatedContent = JSON.parse(modifiedValue);
    } catch {
      showSnackbar("Cannot save: Invalid JSON", true);
      return;
    }

    const matchId = displayedMatch.id || displayedMatch.consultant_id;
    let saveResult: Awaited<ReturnType<typeof alqemiDataService.saveCv>> | null =
      null;
    if (matchId) {
      saveResult = await alqemiDataService.saveCv(matchId, updatedContent);
    }

    if (setCvContent) {
      setCvContent(updatedContent);
    }

    setNormalizedCvContent(updatedContent);

    if (offer && setOffer) {
      const updatedOffer = {
        ...offer,
        matches: offer.matches?.map((match) =>
          match.id === displayedMatch.id
            ? {
                ...match,
                cv: updatedContent,
                keyword_analysis:
                  saveResult?.keyword_analysis ?? match.keyword_analysis,
              }
            : match
        ),
      };
      setOffer(updatedOffer);
    }

    setHasUnsavedChanges(false);
    onUnsavedChangesChange?.(false);
    showSnackbar("Changes saved successfully", false, true);
  };

  handleSaveChangesRef.current = handleSaveChanges;

  if (!displayedMatch) {
    return null;
  }

  if (isLoading) {
    return <CircularLoader />;
  }

  if (!normalizedCvContent || !cvTemplateFull) {
    if (cvContent && Object.keys(cvContent).length > 0 && cvTemplateFull) {
      return <CircularLoader />;
    }
    return (
      <Stack
        sx={{ justifyContent: "center", alignItems: "center", height: "100%" }}
      >
        No CV available for comparison.
      </Stack>
    );
  }

  const formattedCvContent = JSON.stringify(
    canonicalizeKeys(normalizedCvContent, propertyOrder),
    null,
    2
  );
  const formattedCvTemplate = JSON.stringify(
    canonicalizeKeys(
      alignArraysToReference(cvTemplateFull, normalizedCvContent),
      propertyOrder
    ),
    null,
    2
  );

  return (
      <Stack sx={{ height: "100%", border: "1px solid #e0e0e0", position: "relative" }}>
        <DiffEditor
          original={formattedCvTemplate}
          modified={formattedCvContent}
          language="json"
          theme="light"
          loading={<CircularLoader />}
          onMount={handleEditorDidMount}
          options={{
            readOnly: false,
            renderSideBySide: false,
            wordWrap: "on",
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            folding: true,
            fontSize: 13,
            fontFamily: "monospace",
            lineNumbers: "on",
            originalEditable: false,
          }}
        />
        {hasUnsavedChanges && (
          <>
            <Button
              variant="contained"
              size="small"
              onClick={handleSaveChanges}
              startIcon={<SaveIcon />}
              sx={{
                position: "absolute",
                bottom: 16,
                right: 16,
                zIndex: 10,
                backgroundColor: "#ff5950",
                "&:hover": {
                  backgroundColor: "#e54842",
                },
                boxShadow: 2,
                textTransform: "none",
              }}
            >
              Save
            </Button>
            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                bottom: 52,
                right: 16,
                zIndex: 10,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                padding: "4px 19px",
                color: "#666",
              }}
            >
              {navigator.platform.includes("Mac") ? "Cmd+S" : "Ctrl+S"}
            </Typography>
          </>
        )}
      </Stack>
  );
});

export default CvJsonDiffComparer;
